import React from 'react';
import { Button, FormInput, FormTextarea, Message } from '@getro/rombo';
import { Formik, Form, Field } from 'formik';
import { ChevronLeft } from 'lucide-react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Link, useHistory } from 'react-router-dom';

import { useCompaniesFormValidation } from 'pages/list/hooks/useCompaniesFormValidation';
import { useEditCompanyList } from '../hooks/useEditCompanyList';

export const AddCompanies = ({ activeNetwork, sharedList }) => {
  const { initialValues, updateSharedList, isUpdatingList, isError } = useEditCompanyList(sharedList);
  const history = useHistory();

  const { onFormValidation } = useCompaniesFormValidation();

  const onSubmit = async (values) => {
    const { name } = values;
    const domains = values.domains.split(/[\n,]+/);

    updateSharedList({
      name,
      domains: domains.filter((domain) => domain.trim().length > 0),
    }).then(() => {
      history.push(`/networks/${activeNetwork.slug}/list/${sharedList.id}/contacts`);
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        as={Link}
        to={`/networks/${activeNetwork.slug}/list/${sharedList.id}/contacts/settings${window.location.search}`}
        sx={{ textDecoration: 'none', fontWeight: 'body', position: 'absolute', top: '-20px', left: '0' }}
      >
        <Flex sx={{ alignItems: 'center', color: 'text.subtle', gap: '8px' }}>
          <Box as={ChevronLeft} width="16px" height="16px" aria-hidden="true" sx={{ strokeWidth: 1.5 }} />
          <Text fontSize="12px" fontWeight="medium" lineHeight="20px">
            Back
          </Text>
        </Flex>
      </Box>
      <Text fontSize="20px" fontWeight="semibold" color="text.dark" lineHeight="28px">
        Manage companies in list
      </Text>
      <Box mt="8px">
        <Formik validate={onFormValidation} initialValues={initialValues} onSubmit={onSubmit}>
          {({ values }) => (
            <Box as={Form} sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Field name="name" component={FormInput} type="hidden" />
              <Field
                height="120px"
                name="domains"
                component={FormTextarea}
                label="Companies websites"
                helpText="Enter the websites of the companies you want to add, one per line or separated by commas. You can easily copy and paste them from a spreadsheet."
                placeholder={`website1.com\nwebsite2.com\nwebsite3.com\n...`}
                value={values?.domains?.replace(/,/g, '\n')}
                containerProps={{
                  sx: {
                    'div[color="danger"]': {
                      bg: 'red.20',
                      p: '8px',
                      '& > div': {
                        p: '0px',
                      },
                    },
                  },
                }}
              />
              <Box>
                <Button type="submit" size="small" loading={isUpdatingList}>
                  Update list
                </Button>
              </Box>
              {isError && (
                <Box>
                  <Message type="error" title="Invalid website format">
                    There was an unexpected error, please try again or{' '}
                    <Box as="a" href="mailto:help@getro.com">
                      contact us
                    </Box>
                    .
                  </Message>
                </Box>
              )}
            </Box>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

AddCompanies.propTypes = {
  activeNetwork: PropTypes.object,
  sharedList: PropTypes.object,
};

AddCompanies.defaultProps = {
  activeNetwork: {},
  sharedList: {},
};
